import { useAtomValue } from "jotai";
import type { PageProps } from "next";
import { useEffect } from "react";
import { graphql, usePaginationFragment, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";

import { FallbackLoading } from "~/components/fallback-loading";
import { PostiesSectionSeo } from "~/components/posties/posties-section-seo";
import { trackingAtom } from "~/components/tracking";
import { useInfiniteScrollTriggerReference } from "~/lib/hooks";
import { createClientEnvironment } from "~/lib/relay/environment.client";
import { parseQueryString } from "~/lib/utils";
import type { sectionIndexArticleList$key } from "~/queries/__generated__/sectionIndexArticleList.graphql";
import type { sectionIndexArticleListPaginationQuery } from "~/queries/__generated__/sectionIndexArticleListPaginationQuery.graphql";
import type { sectionIndexPageQuery } from "~/queries/__generated__/sectionIndexPageQuery.graphql";

import {
  Container,
  Content,
  Headline,
  Line,
  StyledAppFooter,
  StyledContentItemPostiesSectionPrimary,
  StyledContentItemPostiesSectionSecondary,
  StyledLoading,
  WelcomeMessage,
  WelcomeMessageLargeDesktop,
} from "./styles";

const query = graphql`
  query sectionIndexPageQuery(
    $queueName: String!
    $entityId: String!
    $after: String
    $first: Int
  ) {
    section(filter: { entityId: $entityId }) {
      name
      ...sectionSeoSection
    }
    queue(filter: { name: $queueName }) {
      ...sectionIndexArticleList @arguments(after: $after, first: $first)
    }
  }
`;

const PostiesSectionIndexPage: RelayComponent<unknown, sectionIndexPageQuery> = ({
  preloadedQuery,
}) => {
  const { trackPageView } = useAtomValue(trackingAtom);

  useEffect(() => {
    trackPageView?.();
  }, [trackPageView]);

  const preloadedData = usePreloadedQuery(query, preloadedQuery);
  const {
    data,
    hasNext: hasNextPage,
    isLoadingNext: isLoading,
    loadNext,
  } = usePaginationFragment<sectionIndexArticleListPaginationQuery, sectionIndexArticleList$key>(
    graphql`
      fragment sectionIndexArticleList on Queue
      @argumentDefinitions(after: { type: "String" }, first: { type: "Int", defaultValue: 10 })
      @refetchable(queryName: "sectionIndexArticleListPaginationQuery") {
        items(first: $first, after: $after)
          @connection(key: "sectionIndexArticleListQuery__items") {
          edges {
            node {
              ... on Article {
                ...postiesSectionPrimaryContentItemContent
                ...postiesSectionSecondaryContentItemContent
              }
            }
          }
        }
      }
    `,
    preloadedData.queue,
  );

  const { infiniteScrollTriggerReference } = useInfiniteScrollTriggerReference({
    hasNextPage,
    isLoading,
    onLoadMore: () => loadNext(10),
  });

  if (!data) return null;

  const [primaryNode, ...secondaryNodes] = data?.items?.edges ?? [];

  return (
    <>
      <PostiesSectionSeo reference={preloadedData.section} />
      <Container>
        <WelcomeMessageLargeDesktop />
        <Headline>{preloadedData.section.name}</Headline>
        <Content>
          <WelcomeMessage />
          {primaryNode && <StyledContentItemPostiesSectionPrimary reference={primaryNode.node} />}
          {secondaryNodes.length > 0 && (
            <>
              <Line />
              {secondaryNodes.map(({ node }, index) => (
                <StyledContentItemPostiesSectionSecondary key={index} reference={node} />
              ))}
            </>
          )}
          {hasNextPage && (
            <StyledLoading ref={element => infiniteScrollTriggerReference(element)}>
              LOADING ...
            </StyledLoading>
          )}
        </Content>
        {!hasNextPage && <StyledAppFooter variant="posties" />}
      </Container>
    </>
  );
};

PostiesSectionIndexPage.displayName = "PostiesSectionIndex";

const pageProps: PageProps = {
  appBarConfiguration: {
    variant: "posties/generic",
  },
  appInitializerConfiguration: {
    application: "posties",
  },
};

export default withRelay(PostiesSectionIndexPage, query, {
  clientSideProps: () => pageProps,
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("~/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: () => Promise.resolve(pageProps),
  variablesFromContext(context) {
    const entityId = parseQueryString(context.query.entityId) ?? "";
    return {
      entityId,
      first: 10,
      name: context.query.name,
      queueName: `section_top_${entityId}`,
    };
  },
});
